import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { State } from 'src/app/app.reducer';
import { APIService } from 'src/app/core/api/general.service';
import { PaymentType } from 'src/app/core/enums/enums';
import { IClientSettings } from 'src/app/core/models/agency-settings.model';
import { DOCSADoc, DOCSARequest } from 'src/app/core/models/docsa.model';
import { EmergencyContactModel } from 'src/app/core/models/emergency-contact.model';
import { FareFamiliesState } from 'src/app/core/models/fare-family.model';
import { ExchangeOptionFilter } from 'src/app/core/models/involuntary-change.model';
import { ContractError, MiniState } from 'src/app/core/models/models';
import { Step } from 'src/app/core/models/step.model';
import { ReissueService } from 'src/app/reissue/services/reissue.service';
import { PnrActions } from 'src/app/search-result/actions';
import { ItineraryService } from 'src/app/search-result/services/itinerary.service';
import { SettingsService } from 'src/app/settings/services/settings.service';
import { VoluntaryChangeActions } from '../actions';
import * as fromReducer from '../reducers/voluntary-change.reducer';
import { MapItineraryApi } from 'src/app/core/api/mappers/map-itinerary-api.service';
import {
  FeedbackModelML,
  PenaltyFareBasis,
  PenaltyFareBasisMachineLearning,
  Price
} from 'src/app/core/models/cancellation.model';
import {
  Flight,
  Location,
  PnrInfo,
  QuoteRequest,
  Segment
} from 'src/app/core/models/itinerary.model';
import {
  ExchangeAvailability,
  ExchangeAvailabilityOption,
  ExchangeQuoteRequest,
  ExchangeSegment,
  IPricingError,
  IPricingRequestPayload,
  IssueAncillaries,
  IVoluntaryReproState,
  VoluntaryExchangeQuote
} from 'src/app/core/models/voluntary-change.model';

@Injectable()
export class VoluntaryChangeService {
  private clienSettings$: Observable<IClientSettings>;
  isAirline: Observable<boolean>;

  constructor(
    private store: Store<State>,
    private itinerary: ItineraryService,
    private settingsService: SettingsService,
    private router: Router,
    private reissueService: ReissueService,
    private apiService: APIService,
    private mapItineraryApi: MapItineraryApi
  ) {
    this.isAirline = this.settingsService.getClientIsAirline();
    this.clienSettings$ = this.settingsService.getClientSettings();
  }

  getVoluntaryExchangeQuote(): Observable<VoluntaryExchangeQuote> {
    return this.store.pipe(select(fromReducer.getVoluntaryExchangeQuote));
  }

  getNotExchangeAllowed(): Observable<boolean> {
    return this.store.pipe(select(fromReducer.getNotExchangeAllowed));
  }

  getAlternativeFlow(): Observable<boolean> {
    return this.store.pipe(select(fromReducer.getAlternativeFlow));
  }

  getIsNewFlow(): Observable<boolean> {
    return this.store.pipe(select(fromReducer.getIsNewFlow));
  }

  setAlternativeFlowValue(value: boolean): void {
    this.store.dispatch(VoluntaryChangeActions.SetAlternativeFlow({ value }));
  }

  setIsNewFlowValue(value: boolean): void {
    this.store.dispatch(VoluntaryChangeActions.SetIsNewFlow({ value }));
  }

  resetNotExchangeAllowed(): void {
    this.store.dispatch(VoluntaryChangeActions.ResetNotExchangeAllowed());
  }

  getError(): Observable<ContractError> {
    return this.store.pipe(select(fromReducer.getError));
  }

  getQuoteRequest(): Observable<QuoteRequest> {
    return this.itinerary.getQuoteRequest();
  }

  getLoading(): Observable<boolean> {
    return this.store.pipe(select(fromReducer.getLoading));
  }

  getSteps(): Observable<Step[]> {
    return this.store.pipe(select(fromReducer.getSteps));
  }

  getActiveStep(): Observable<number> {
    return this.store.pipe(select(fromReducer.getActiveStep));
  }

  getOriginalSelectedSegments(): Observable<ExchangeSegment[]> {
    return this.store.pipe(select(fromReducer.getOriginalSelectedSegments));
  }

  /**
   * Detail modal
   */

  getOpenDetailModal(): Observable<boolean> {
    return this.store.pipe(select(fromReducer.getOpenDetailModal));
  }

  dispatchOpenDetailModal(open: boolean) {
    this.store.dispatch(VoluntaryChangeActions.OpenCloseDetailModal({ open }));
  }

  /**
   * Availabilities
   */
  dispatchLoadAvailability(form: ExchangeQuoteRequest) {
    this.settingsService
      .getClientSettings()
      .pipe(take(1))
      .subscribe((res) => {
        this.getNotExchangeAllowed()
          .pipe(take(1))
          .subscribe((isNotExchangeAllowed) =>
            this.store.dispatch(
              VoluntaryChangeActions.LoadAvailability({
                form,
                isVoluntary: !isNotExchangeAllowed,
                needModel: res.machineLearningEnabled
              })
            )
          );
      });
  }

  updateAvailabilities(availabilities) {
    return VoluntaryChangeActions.LoadAvailabilitySuccess({ availabilities });
  }

  getExchangeAvailability(): Observable<ExchangeAvailability> {
    return this.store.pipe(select(fromReducer.getExchangeAvailability));
  }

  getPenaltiesAdditional(): Observable<PenaltyFareBasis[]> {
    return this.store.pipe(select(fromReducer.getHighlightCategories));
  }

  getLoadingPenalties(): Observable<boolean> {
    return this.store.pipe(select(fromReducer.loadingPenaltiesEffect));
  }

  /**
   * DOCSA
   */

  getDocsaDocs(): Observable<DOCSADoc[]> {
    return this.store.pipe(select(fromReducer.selectDocsaDocs));
  }

  getDocsaRequest(): Observable<DOCSARequest> {
    return this.store.pipe(select(fromReducer.selectDocsaRequest));
  }

  getNeedsDocsa(): Observable<boolean> {
    return this.store.pipe(select(fromReducer.selectNeedsDocsa));
  }

  dispatchSetLoadingVoluntaryChange(isLoading: boolean): void {
    this.store.dispatch(VoluntaryChangeActions.setLoadingVoluntaryChange({ isLoading }));
  }

  dispatchSetVoluntaryReproState(voluntaryReproState: IVoluntaryReproState): void {
    this.store.dispatch(VoluntaryChangeActions.setVoluntaryReproState({ voluntaryReproState }));
  }

  dispatchSaveDocsa(docs: DOCSADoc[]) {
    this.store.dispatch(VoluntaryChangeActions.DocsaSave({ docs }));
  }

  dispatchDocsaRequest() {
    this.store.dispatch(VoluntaryChangeActions.DocsaRequest());
  }

  dispatchExchangePricing(payload: IPricingRequestPayload): void {
    this.store.dispatch(VoluntaryChangeActions.ExchangePricingRequest({ payload }));
  }

  dispatchUpdateExchangePricing(exchangePricing: ExchangeAvailabilityOption) {
    this.store.dispatch(VoluntaryChangeActions.ExchangePricingSuccess({ exchangePricing }));
  }

  saveAlternative(option: ExchangeAvailabilityOption, index: number) {
    this.saveOption(option, index);
  }

  dispatchChooseAvailabilityNext(option: ExchangeAvailabilityOption, index: number) {
    this.saveOption(option, index);
    this.isAirline
      .subscribe((isAirline) => {
        this.itinerary
          .getPnrInfo()
          .pipe(take(1))
          .subscribe((pnr) => {
            this.mapItineraryApi.needAditionalInformation(pnr.passengers) ||
            pnr?.isDocsRequired ||
            pnr?.emergencyContactRequired
              ? this.store.dispatch(VoluntaryChangeActions.GoToDocsa({ isAirline }))
              : this.dispatchGoToSeatMap();
          });
      })
      .unsubscribe();
  }

  saveOption(option: ExchangeAvailabilityOption, index: number) {
    this.dispatchSaveOption(option, index);
  }

  dispatchGoToSeatMap() {
    this.store.dispatch(VoluntaryChangeActions.GoToSeatMap());
  }

  dispatchGoToDocsaStep(isAirline: boolean) {
    this.store.dispatch(VoluntaryChangeActions.GoToDocsa({ isAirline }));
  }

  dispatchGoToConfirmation() {
    this.isAirline
      .subscribe((isAirline) => {
        this.store.dispatch(VoluntaryChangeActions.GoToConfirmation({ isAirline }));
      })
      .unsubscribe();
  }

  dispatchSaveEmergencyContact(contact: EmergencyContactModel[]) {
    this.store.dispatch(VoluntaryChangeActions.EmergencyContact({ contact }));
  }

  dispatchSaveOption(option: ExchangeAvailabilityOption, index: number) {
    this.store.dispatch(VoluntaryChangeActions.SaveOption({ option, index }));
  }

  dispatchStoreLoadingPenalties() {
    this.store.dispatch(VoluntaryChangeActions.loadingPenaltiesRequest());
  }

  dispatchStoreLoadingPenaltiesML() {
    return this.store.dispatch(VoluntaryChangeActions.loadingPenaltiesMLRequest());
  }

  dispatchClearVoluntaryChange() {
    this.store.dispatch(VoluntaryChangeActions.clearVoluntaryChange());
  }

  dispatchStorePenaltiesRequestExchange(penaltyFareBasis: PenaltyFareBasis[]) {
    this.store.dispatch(VoluntaryChangeActions.loadPenaltiesSuccess({ penaltyFareBasis }));
  }

  dispatchSendFeedbackMLequest(feedback: FeedbackModelML) {
    this.store.dispatch(VoluntaryChangeActions.sendFeedbackML({ feedback }));
  }

  dispatchStorePenaltiesMLRequest(penaltyMLFareBasis: PenaltyFareBasisMachineLearning) {
    this.store.dispatch(VoluntaryChangeActions.loadPenaltiesMLSuccess({ penaltyMLFareBasis }));
  }

  dispatchStoreErrorOnLoadPenaltiesML(error: ContractError) {
    this.store.dispatch(VoluntaryChangeActions.LoadPenaltiesMLFail({ error }));
  }

  /**
   * *Info: This method is not needed at the moment but it is important to keep
   * *in the future could be necessary to validate DOCSA again
   */
  private needDocsa(segments: Segment[]) {
    return segments.reduce(
      (res, seg) =>
        seg.flights.reduce(
          (re, flight) =>
            (this.isUSAorMexico(flight.arrivalLocation) &&
              !this.isSameCountry(flight.arrivalLocation, flight.departureLocation)) ||
            (this.isUSAorMexico(flight.departureLocation) &&
              !this.isSameCountry(flight.arrivalLocation, flight.departureLocation)) ||
            re,
          false
        ) || res,
      false
    );
  }

  private isUSAorMexico(location: Location): boolean {
    return location.country === 'MX' || location.country === 'US';
  }

  private isSameCountry(location: Location, location2: Location): boolean {
    return (
      (location.country === 'US' && location2.country === 'US') ||
      (location.country === 'MX' && location2.country === 'MX')
    );
  }

  /**
   * Split
   */
  getSplitState(): Observable<MiniState> {
    return this.store.pipe(select(fromReducer.getSplitState));
  }

  dispatchGoBackFromSplit() {
    this.store.dispatch(VoluntaryChangeActions.GoBackFromSplit());
  }

  dispatchRequestSplit(quoteRequest: QuoteRequest) {
    this.store.dispatch(PnrActions.SplitPnrRequest({ quoteRequest }));
  }

  dispatchContinueFromSplit() {
    this.store.dispatch(VoluntaryChangeActions.ContinueFromSplit());
  }

  dispatchContinueFromAditionalInformation() {
    this.store.dispatch(VoluntaryChangeActions.ContinueFromAditionalInformation());
  }

  /**
   * Voluntary Change Request
   */

  getFinalState(): Observable<MiniState> {
    return this.store.pipe(select(fromReducer.getFinalState));
  }

  getIsException(): Observable<boolean> {
    return this.store.pipe(select(fromReducer.getIsException));
  }

  dispatchAddComission(comission: number) {
    this.store.dispatch(VoluntaryChangeActions.AddComission({ comission }));
  }

  dispatchVoluntaryChangeRequest() {
    this.getNotExchangeAllowed()
      .pipe(take(1))
      .subscribe((isNotExchangeAllowed) =>
        this.store.dispatch(
          VoluntaryChangeActions.VoluntaryChangeRequest({
            isVoluntary: !isNotExchangeAllowed
          })
        )
      );
  }

  /**
   * General dispatchers
   */

  dispatchReturnToStep1() {
    this.store.dispatch(VoluntaryChangeActions.ReturnToStep1());
  }

  dispatchCameFromManualFlow() {
    this.store.dispatch(VoluntaryChangeActions.CameFromManualFlow());
  }

  dispatchReturnToManualFlow() {
    this.store.dispatch(VoluntaryChangeActions.ReturnToManualFlow());
  }

  dispatchLoadVoluntaryQuoteSuccess(
    voluntaryExchangeQuote: VoluntaryExchangeQuote,
    quote: QuoteRequest,
    isReissueActive: boolean,
    isAirline: boolean,
    isReissue: boolean = false,
    redirectFromManual: boolean = false
  ) {
    this.store.dispatch(
      VoluntaryChangeActions.LoadVoluntaryQuoteSuccess({
        voluntaryExchangeQuote,
        quote,
        isReissueActive,
        isAirline,
        isReissue,
        redirectFromManual
      })
    );
  }

  dispatchGoToSplitOrRequest() {
    this.store.dispatch(VoluntaryChangeActions.GoToSplitOrRequest());
  }

  goToItinerary() {
    this.itinerary.navigateToWithParams('/itinerary');
  }

  /**
   * Fare families
   */

  dispatchLoadFareFamiliesVoluntaryDetails(
    option: ExchangeAvailabilityOption,
    index: number,
    open = false
  ) {
    this.store.dispatch(VoluntaryChangeActions.LoadFareFamiliesVoluntary({ option, index, open }));
  }

  dispatchLoadFareFamiliesVoluntary(
    option: ExchangeAvailabilityOption,
    index: number,
    open = false
  ) {
    this.clienSettings$.subscribe((settings) => {
      if (settings?.showFamilyFareEnabled) {
        this.store.dispatch(
          VoluntaryChangeActions.LoadFareFamiliesVoluntary({ option, index, open })
        );
      }
    });
  }

  getFareFamiliesState(): Observable<FareFamiliesState> {
    return this.store.pipe(select(fromReducer.getFareFamiliesState));
  }

  getIndex(): Observable<number> {
    return this.store.pipe(select(fromReducer.getIndex));
  }

  getOverrideSegments(): Observable<number[]> {
    return this.itinerary.getOverrideSegments();
  }

  getOptionExchangePricing(): Observable<ExchangeAvailabilityOption> {
    return this.store.pipe(select(fromReducer.selectExchangePricingOption));
  }

  getFarebasisFromSegment(segment: Segment | any): string[] {
    return segment.flights.reduce(
      (res: string[], flight: Flight) =>
        res.includes(flight.fareBasis) ? res : res.concat(flight.fareBasis),
      []
    );
  }

  /**
   * Tax code and Farebasis
   */

  saveMoreRestrictiveFarebasis(farebasis: string) {
    this.store.dispatch(VoluntaryChangeActions.SaveFarebasis({ farebasis }));
  }

  /**
   * SaveFilters
   */

  saveFilters(filter: ExchangeOptionFilter) {
    this.store.dispatch(VoluntaryChangeActions.SaveFilter({ filter }));
  }

  //#region Reissue
  goToReissue() {
    this.reissueService.dispatchSetBookingType(true);
    this.getFinalState()
      .pipe(take(1))
      .subscribe((finalState) => {
        if (finalState?.request.selectedOption?.exchangeFare?.totalAmount.amount >= 0) {
          this.router.navigateByUrl('reissue');
        } else {
          this.dispatchReissueFromVoluntary();
          this.router.navigateByUrl('reissue/confirmation');
        }
      });
  }

  dispatchReissueFromVoluntary() {
    this.reissueService
      .getIsAutomatic$()
      .pipe(take(1))
      .subscribe((response) => {
        response
          ? this.reissueService.dispatchAutomaticBooking(PaymentType.Cash, null)
          : this.reissueService.dispatchManualBooking(PaymentType.Cash, null);
      });
  }

  //#endregion

  // Machine Learning

  getPenaltiesMLFarebasis() {
    return this.store.pipe(select(fromReducer.getPenaltyMLFareBasis));
  }

  validateMachineLearningModel(): Observable<boolean> {
    return this.store.pipe(select(fromReducer.validateMachineLearningModel));
  }

  dispatchSetRemoveSegments(removeSegment: number): void {
    this.store.dispatch(VoluntaryChangeActions.setRemoveSegments({ removeSegment }));
  }

  dispatchRestoreAllRemovedSegments(segments: ExchangeSegment[]): void {
    this.store.dispatch(VoluntaryChangeActions.restoreAllRemovedSegments({ segments }));
  }

  dispatchClearRemoveSegments() {
    this.store.dispatch(VoluntaryChangeActions.clearRemoveSegments());
  }

  getErrorPenaltyMLFareBasis(): Observable<ContractError> {
    return this.store.pipe(select(fromReducer.getErrorPenaltyMLFareBasis));
  }

  getLoadingPenaltyMLFareBasis(): Observable<boolean> {
    return this.store.pipe(select(fromReducer.getLoadingPenaltyMLFareBasis));
  }

  //#endregion

  getIsPricingError(): Observable<IPricingError> {
    return this.store.pipe(select(fromReducer.getIsPricingError));
  }

  dispatchUpdatePricingQuoteRequestWithSeatPrices(reservationSeatPrices: Price[]) {
    this.store.dispatch(
      VoluntaryChangeActions.UpdatePricingQuoteRequestWithSeatPrices({
        prices: reservationSeatPrices
      })
    );
  }

  getExchangeTotalAmount(): Observable<Price> {
    return this.store.pipe(select(fromReducer.getExchangeTotalAmount));
  }

  dispatchFulfillConsult(quoteMap: any, remarks: any, paymentForm: any) {
    return this.apiService.reissue(quoteMap, remarks, paymentForm);
  }

  dispatchFulfillConsultRedux(quoteMap: any, remarks: any, paymentForm: any) {
    this.store.dispatch(
      VoluntaryChangeActions.dispatchFulfillConsult({ quoteMap, remarks, paymentForm })
    );
  }

  getFulfillAPIResponse(): Observable<PnrInfo> {
    return this.store.pipe(select(fromReducer.getFulfillAPIResponse));
  }

  dispatchIssueAncillaries(issueAncillaries: IssueAncillaries) {
    return this.apiService.issueAncillaries(issueAncillaries);
  }

  dispatchIsOriginalFare(isOriginalFare: boolean) {
    this.store.dispatch(VoluntaryChangeActions.setIsOriginalFare({ isOriginalFare }));
  }

  dispatchUpdateVoluntaryChangeSuccess(pnr: PnrInfo) {
    this.store.dispatch(
      VoluntaryChangeActions.VoluntaryChangeSuccess({
        pnr
      })
    );
  }

  getLoadingVoluntaryProcess(): Observable<boolean> {
    return this.store.pipe(select(fromReducer.getLoadingVoluntaryProcess));
  }

  getVoluntaryReproState(): Observable<IVoluntaryReproState> {
    return this.store.pipe(select(fromReducer.getVoluntaryReproState));
  }

  getSelectedOption(): Observable<ExchangeAvailabilityOption> {
    return this.store.pipe(select(fromReducer.getSelectedOption));
  }

  getAvailabilitiesOptions(): Observable<ExchangeAvailabilityOption[]> {
    return this.store.pipe(select(fromReducer.getAvailabilitiesOptions));
  }
}
